import { CostBasisMethodMap, IncomeLetterCodeMap, InvestmentGoalKeyMap } from '@interfaces';
import { CostBasisPosition } from '@interfaces/cost-basis/cost-basis.service.interface';
import { TransactionTypesEnum } from '@interfaces/models/selected-client-accounts.interface';
import {
  AccountTypesFull,
  AccountTypesToFullMap,
} from '@interfaces/submit/internal-transfer-co0-payload.interface';
import { formatSharesToTransfer } from '@shared/helpers/internal-transfer-calculations';
import { CostBasisMethods, Move_To_New } from '@shared/models';
import type { Holding, InvestmentGoals } from 'coo-transactions-common-library/dist/models';
import { AmountIndicator } from 'coo-transactions-common-library/dist/models/amount-indicator';

export interface HoldingWithFilenetData extends Holding {
  ticker?: string;
  securityName?: string;
  percentOfHolding?: string;
  /** Shares formatted at the fund-type's required decimal precision, for the Alfresco receipt only */
  sharesToTransferDisplay?: string;
}

export const resolveToAccountType = (
  transactionType: TransactionTypesEnum,
  existingAccountType?: string,
): AccountTypesFull | undefined => {
  if (!!existingAccountType) {
    return AccountTypesToFullMap[existingAccountType];
  }
  if (Move_To_New.has(transactionType)) {
    return AccountTypesFull.INDV;
  }
  return undefined;
};

export const mapPositions = (positions: CostBasisPosition[]): HoldingWithFilenetData[] => {
  return positions.map((position) => ({
    positionId: position.positionId,
    giftingMethod: AmountIndicator.SHARES,
    costBasisMethod: CostBasisMethodMap[position.selectedCostBasisMethod ?? CostBasisMethods.NONE],
    shownCostBasisMethod:
      CostBasisMethodMap[position.shownCostBasisMethod ?? CostBasisMethods.NONE],
    amount: String(position.sharesToTransfer ?? ''), //CO0 wants shares only for IT - calculated earlier in flow
    taxLots: [],
    additionalTaxLotId: '',
    ticker: position.ticker ?? '',
    securityName: position.securityName ?? '',
    selectedAmount: Number(position.estValueOfTransfer) || 0,
    percentOfHolding: String(position.percentOfHolding ?? ''),
    sharesToTransferDisplay:
      position.sharesToTransfer != null ? formatSharesToTransfer(position) : '',
  }));
};

export const buildInvestmentGoals = (
  objectives: { selectedObjectives: { key: string }[] } | null | undefined,
  financials: { annualIncome?: string; liquidNetWorth?: string } | undefined,
): InvestmentGoals | undefined => {
  if (!objectives?.selectedObjectives?.length) {
    return undefined;
  }

  return {
    goals: objectives.selectedObjectives
      .map((obj) => InvestmentGoalKeyMap[obj.key])
      .filter(Boolean),
    annualIncome: financials?.annualIncome
      ? IncomeLetterCodeMap[financials.annualIncome]
      : undefined,
    liquidNetWorth: financials?.liquidNetWorth
      ? IncomeLetterCodeMap[financials.liquidNetWorth]
      : undefined,
  };
};
